import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Mail, Printer, Plus, Pencil, Trash2, Search, FilterX, Download, Send, FileText, BookmarkPlus, Phone, MapPin, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ExportMenu from "@/components/ExportMenu";
import EmptyState from "@/components/EmptyState";
import AiDraftLetterButton from "@/components/AiDraftLetterButton";

const STATUS_META = {
  draft:   { label: "Draft",   color: "bg-slate-100 text-slate-700 border-slate-300" },
  printed: { label: "Printed", color: "bg-blue-50 text-blue-700 border-blue-200" },
  sent:    { label: "Sent",    color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const BLANK_LETTER = {
  type: "letter",
  subject: "",
  body: "",
  recipient_name: "",
  recipient_email: "",
  recipient_address: "",
  tenant_id: "",
  property_id: "",
  template_id: "",
};

const BLANK_TEMPLATE = { name: "", description: "", subject: "", body: "" };

export default function LettersPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("log"); // log | templates
  const [letters, setLetters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [meta, setMeta] = useState({ company: {}, merge_fields: [] });

  const [filters, setFilters] = useState({ type: "all", status: "all", date_from: "", date_to: "", q: "" });

  // Compose
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_LETTER);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Template create/edit
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState(BLANK_TEMPLATE);
  const [tplEditing, setTplEditing] = useState(null);
  const [tplConfirmDelete, setTplConfirmDelete] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (filters.type !== "all") p.type = filters.type;
    if (filters.status !== "all") p.status = filters.status;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.q.trim()) p.q = filters.q.trim();
    return p;
  }, [filters]);

  const loadLetters = async () => {
    const { data } = await api.get("/letters", { params });
    setLetters(data);
  };
  const loadTemplates = async () => {
    const { data } = await api.get("/letter-templates");
    setTemplates(data);
  };

  useEffect(() => {
    (async () => {
      const [m, t, p, _t, _tpl] = await Promise.all([
        api.get("/letters/meta"),
        api.get("/tenants").catch(() => ({ data: [] })),
        api.get("/properties").catch(() => ({ data: [] })),
        loadLetters(),
        loadTemplates(),
      ]);
      setMeta(m.data); setTenants(t.data); setProperties(p.data);
    })();
  }, []);
  useEffect(() => { loadLetters(); }, [params]); // eslint-disable-line

  // -------- Compose handlers --------
  const openCompose = (preset = null) => {
    if (preset) {
      setEditing(null);
      setForm({ ...BLANK_LETTER, subject: preset.subject, body: preset.body, template_id: preset.id });
    } else {
      setEditing(null);
      setForm(BLANK_LETTER);
    }
    setOpen(true);
  };
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      type: l.type || "letter",
      subject: l.subject || "",
      body: l.body || "",
      recipient_name: l.recipient_name || "",
      recipient_email: l.recipient_email || "",
      recipient_address: l.recipient_address || "",
      tenant_id: l.tenant_id || "",
      property_id: l.property_id || "",
      template_id: l.template_id || "",
    });
    setOpen(true);
  };

  const submitLetter = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/letters/${editing.id}`, form);
        toast.success("Letter saved");
      } else {
        await api.post("/letters", form);
        toast.success("Letter saved as draft");
      }
      setOpen(false); setForm(BLANK_LETTER); setEditing(null);
      loadLetters();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const downloadPdf = async (l, andMarkPrinted = true) => {
    try {
      const res = await api.get(`/letters/${l.id}/render`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      const safe = (l.subject_rendered || l.subject || "letter").replace(/[^A-Za-z0-9]+/g, "-").toLowerCase().slice(0, 40);
      a.href = url; a.download = `rojasproperty-${safe}-${today}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      if (andMarkPrinted) {
        await api.post(`/letters/${l.id}/mark-printed`).catch(() => {});
        loadLetters();
      }
      toast.success("PDF downloaded");
    } catch { toast.error("Download failed"); }
  };

  const printPdf = async (l) => {
    try {
      const res = await api.get(`/letters/${l.id}/render`, { params: { inline: true }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (w) w.focus();
      await api.post(`/letters/${l.id}/mark-printed`).catch(() => {});
      loadLetters();
    } catch { toast.error("Could not open print preview"); }
  };

  const openSend = (l) => {
    setSendTarget(l);
    setSendTo(l.recipient_email || "");
    setSendOpen(true);
  };
  const sendNow = async () => {
    if (!sendTo) { toast.error("Recipient email is required"); return; }
    setSending(true);
    try {
      await api.post(`/letters/${sendTarget.id}/send`, { to_email: sendTo });
      toast.success(`Sent to ${sendTo}`);
      setSendOpen(false); setSendTarget(null);
      loadLetters();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Email failed"); }
    setSending(false);
  };

  const removeLetter = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/letters/${confirmDelete.id}`);
      toast.success("Letter deleted");
      setConfirmDelete(null);
      loadLetters();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  // -------- Template handlers --------
  const submitTemplate = async (e) => {
    e.preventDefault();
    if (!tplForm.name.trim() || !tplForm.subject.trim() || !tplForm.body.trim()) {
      toast.error("Name, subject and body are required");
      return;
    }
    try {
      if (tplEditing) {
        await api.patch(`/letter-templates/${tplEditing.id}`, tplForm);
        toast.success("Template updated");
      } else {
        await api.post("/letter-templates", tplForm);
        toast.success("Template saved");
      }
      setTplOpen(false); setTplForm(BLANK_TEMPLATE); setTplEditing(null);
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
  };
  const removeTemplate = async () => {
    if (!tplConfirmDelete) return;
    try {
      await api.delete(`/letter-templates/${tplConfirmDelete.id}`);
      toast.success("Template deleted");
      setTplConfirmDelete(null);
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const resetFilters = () => setFilters({ type: "all", status: "all", date_from: "", date_to: "", q: "" });
  const hasFilters = filters.type !== "all" || filters.status !== "all" || filters.date_from || filters.date_to || filters.q;
  const isAdmin = user?.role === "super_admin" || user?.role === "landlord";

  // -------- Live preview of body w/ merge fields --------
  const previewBody = useMemo(() => {
    const tenant = tenants.find((t) => t.id === form.tenant_id) || null;
    const property = properties.find((p) => p.id === form.property_id) || null;
    const fields = {
      "{{tenant_name}}": tenant ? `${tenant.first_name || ""} ${tenant.last_name || ""}`.trim() : "",
      "{{tenant_first_name}}": tenant?.first_name || "",
      "{{property_name}}": property?.name || "",
      "{{property_address}}": property?.address || "",
      "{{today}}": new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
      "{{sender_name}}": user?.name || "",
      "{{sender_username}}": user?.username || "",
    };
    let out = form.body;
    let sub = form.subject;
    Object.entries(fields).forEach(([k, v]) => { out = out.split(k).join(v); sub = sub.split(k).join(v); });
    return { body: out, subject: sub };
  }, [form, tenants, properties, user]);

  return (
    <AppLayout
      title="Letters & Emails"
      subtitle="Compose, send and archive professional correspondence on Rojas Property letterhead"
      actions={
        tab === "log" ? (
          <div className="flex gap-2">
            <ExportMenu endpoint="/letters/export" filenameBase="rojasproperty-letters" testidPrefix="export-letters" extraParams={params} disabled={letters.length === 0} />
            <Button data-testid="compose-letter-button" onClick={() => openCompose()} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none h-11 px-5 uppercase tracking-[0.2em] text-xs font-semibold gap-2 transition-colors duration-300">
              <Pencil className="h-4 w-4" /> Compose
            </Button>
          </div>
        ) : (
          <Button data-testid="new-template-button" onClick={() => { setTplEditing(null); setTplForm(BLANK_TEMPLATE); setTplOpen(true); }} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none h-11 px-5 uppercase tracking-[0.2em] text-xs font-semibold gap-2 transition-colors duration-300">
            <BookmarkPlus className="h-4 w-4" /> New Template
          </Button>
        )
      }
    >
      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5 border-b border-bone">
        <button data-testid="letters-tab-log" onClick={() => setTab("log")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "log" ? "border-gold text-ink" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Sent Log <span className="ml-1 text-xs text-slate-400">({letters.length})</span>
        </button>
        <button data-testid="letters-tab-templates" onClick={() => setTab("templates")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "templates" ? "border-gold text-ink" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Templates <span className="ml-1 text-xs text-slate-400">({templates.length})</span>
        </button>
      </div>

      {tab === "log" ? (
        <>
          {/* Filters */}
          <Card className="rounded-none p-4 mb-5 border border-bone">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
                  <SelectTrigger data-testid="letters-filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger data-testid="letters-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="printed">Printed</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">From</Label>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">To</Label>
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="letters-filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Subject / recipient…" className="pl-8" />
                </div>
              </div>
            </div>
            {hasFilters && (
              <div className="mt-3 flex justify-end">
                <Button data-testid="letters-clear-filters" variant="ghost" size="sm" onClick={resetFilters} className="gap-1.5 text-slate-500">
                  <FilterX className="h-3.5 w-3.5" /> Clear filters
                </Button>
              </div>
            )}
          </Card>

          {/* List */}
          {letters.length === 0 ? (
            <EmptyState
              kind="notes"
              eyebrow="The Correspondence"
              title="No letters or emails yet."
              body="Compose a professional letter on Rojas Property letterhead — download as PDF, print, or email directly to your tenant or contractor."
              action={<Button onClick={() => openCompose()} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink h-11 px-6 uppercase tracking-[0.2em] text-xs font-semibold transition-colors duration-300">
                <Pencil className="h-4 w-4 mr-2" /> Compose the first letter
              </Button>}
            />
          ) : (
            <div className="space-y-3" data-testid="letters-list">
              {letters.map((l) => {
                const st = STATUS_META[l.status || "draft"] || STATUS_META.draft;
                return (
                  <Card key={l.id} data-testid={`letter-${l.id}`} className="rounded-none p-5 border border-bone hover:border-gold/60 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 border border-bone flex items-center justify-center shrink-0">
                        {l.type === "email" ? <Mail className="h-5 w-5 text-gold" strokeWidth={1.5} /> : <FileText className="h-5 w-5 text-gold" strokeWidth={1.5} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border font-semibold ${st.color}`}>{st.label}</span>
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-slate-300 text-slate-600 font-semibold">{l.type === "email" ? "EMAIL" : "LETTER"}</span>
                        </div>
                        <h4 className="font-display text-lg tracking-tight text-ink">{l.subject_rendered || l.subject}</h4>
                        <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-wrap line-clamp-2">{l.body_rendered || l.body}</p>
                        <div className="text-xs text-slate-500 mt-2">
                          {l.recipient_name && <>To <strong>{l.recipient_name}</strong> · </>}
                          {l.author_username && <>by @{l.author_username} · </>}
                          {(l.created_at || "").slice(0, 16).replace("T", " ")}
                          {l.sent_at && <span className="ml-2 text-emerald-600">Sent {(l.sent_at || "").slice(0, 16).replace("T", " ")}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button onClick={() => downloadPdf(l)} variant="outline" size="sm" data-testid={`download-letter-${l.id}`} className="rounded-none h-9 gap-1.5 border-bone hover:border-gold hover:text-gold">
                          <Download className="h-3.5 w-3.5" /> PDF
                        </Button>
                        <Button onClick={() => printPdf(l)} variant="outline" size="sm" data-testid={`print-letter-${l.id}`} className="rounded-none h-9 gap-1.5 border-bone hover:border-gold hover:text-gold">
                          <Printer className="h-3.5 w-3.5" /> Print
                        </Button>
                        <Button onClick={() => openSend(l)} size="sm" data-testid={`send-letter-${l.id}`} className="rounded-none h-9 gap-1.5 bg-ink text-ivory hover:bg-gold hover:text-ink transition-colors">
                          <Send className="h-3.5 w-3.5" /> Email
                        </Button>
                        {isAdmin && (
                          <div className="flex gap-1 mt-1">
                            <Button onClick={() => openEdit(l)} variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-letter-${l.id}`}>
                              <Pencil className="h-3.5 w-3.5 text-slate-500" />
                            </Button>
                            <Button onClick={() => setConfirmDelete(l)} variant="ghost" size="icon" className="h-8 w-8" data-testid={`delete-letter-${l.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      ) : (
        // TEMPLATES tab
        <>
          {templates.length === 0 ? (
            <EmptyState
              kind="notes"
              eyebrow="Reusable Stationery"
              title="No templates yet."
              body="Save common letters as templates — late notices, welcome letters, lease renewals — and reuse them in seconds with auto-filled tenant + property details."
              action={<Button onClick={() => { setTplEditing(null); setTplForm(BLANK_TEMPLATE); setTplOpen(true); }} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink h-11 px-6 uppercase tracking-[0.2em] text-xs font-semibold transition-colors duration-300">
                <BookmarkPlus className="h-4 w-4 mr-2" /> Create your first template
              </Button>}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-list">
              {templates.map((t) => (
                <Card key={t.id} data-testid={`template-${t.id}`} className="rounded-none p-5 border border-bone hover:border-gold transition-colors">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="micro-label text-gold">{t.created_by_username ? `@${t.created_by_username}` : "Template"}</div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-template-${t.id}`}
                          onClick={() => { setTplEditing(t); setTplForm({ name: t.name, description: t.description || "", subject: t.subject, body: t.body }); setTplOpen(true); }}>
                          <Pencil className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`delete-template-${t.id}`}
                          onClick={() => setTplConfirmDelete(t)}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <h4 className="font-display text-lg tracking-tight text-ink mb-1">{t.name}</h4>
                  <div className="text-xs text-slate-500 mb-3 line-clamp-2">{t.subject}</div>
                  <p className="text-sm text-slate-600 line-clamp-3 mb-4 whitespace-pre-wrap">{t.body}</p>
                  <Button data-testid={`use-template-${t.id}`} onClick={() => { setTab("log"); openCompose(t); }}
                    variant="outline" size="sm" className="w-full rounded-none border-ink hover:bg-gold hover:border-gold hover:text-ink gap-1.5 transition-colors uppercase tracking-[0.2em] text-[10px] font-semibold">
                    <Sparkles className="h-3.5 w-3.5" /> Use Template
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Compose Dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(BLANK_LETTER); setEditing(null); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="letter-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit letter" : "Compose letter"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
            {/* Form */}
            <form onSubmit={submitLetter} className="space-y-3">
              {!editing && templates.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Start from template</Label>
                  <Select value={form.template_id || "none"} onValueChange={(v) => {
                    if (v === "none") { setForm({ ...form, template_id: "" }); return; }
                    const tpl = templates.find((t) => t.id === v);
                    if (tpl) setForm({ ...form, template_id: v, subject: tpl.subject, body: tpl.body });
                  }}>
                    <SelectTrigger data-testid="letter-template-select"><SelectValue placeholder="None — write from scratch" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None — write from scratch</SelectItem>
                      {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="letter-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="letter">Letter (PDF / Print)</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Recipient name</Label>
                  <Input data-testid="letter-recipient-name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Jane Doe" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Property (optional)</Label>
                  <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="letter-property"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tenant (optional)</Label>
                  <Select value={form.tenant_id || "none"} onValueChange={(v) => setForm({ ...form, tenant_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="letter-tenant"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="none">—</SelectItem>
                      {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Recipient email</Label>
                  <Input data-testid="letter-recipient-email" type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="jane@example.com" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Recipient address</Label>
                  <Textarea data-testid="letter-recipient-address" rows={2} value={form.recipient_address} onChange={(e) => setForm({ ...form, recipient_address: e.target.value })} placeholder="Street&#10;City, ST ZIP" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Subject *</Label>
                <Input data-testid="letter-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Body * <span className="text-slate-400">(use {`{{tenant_name}}`}, {`{{property_name}}`}, {`{{today}}`}…)</span></Label>
                  {isAdmin && (
                    <AiDraftLetterButton
                      context={{
                        tenant_name: (() => {
                          const t = tenants.find((x) => x.id === form.tenant_id);
                          return t ? `${t.first_name} ${t.last_name}` : (form.recipient_name || "");
                        })(),
                        property_name: (() => {
                          const p = properties.find((x) => x.id === form.property_id);
                          return p?.name || "";
                        })(),
                        property_address: (() => {
                          const p = properties.find((x) => x.id === form.property_id);
                          return p?.address || "";
                        })(),
                        existing_body: form.body,
                      }}
                      onDraft={({ subject, body }) => setForm((f) => ({
                        ...f,
                        subject: f.subject && f.subject.trim() && f.subject !== subject ? f.subject : subject,
                        body,
                      }))}
                    />
                  )}
                </div>
                <Textarea data-testid="letter-body" required rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Dear {{tenant_name}},&#10;&#10;…" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {meta.merge_fields.slice(0, 7).map((f) => (
                  <button key={f.key} type="button" onClick={() => setForm({ ...form, body: form.body + " " + f.key })}
                    className="text-[10px] uppercase tracking-widest px-2 py-1 border border-bone hover:border-gold hover:text-gold transition-colors">
                    + {f.label}
                  </button>
                ))}
              </div>
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-letter-button" type="submit" disabled={saving} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold">
                  {saving ? "Saving…" : (editing ? "Save changes" : "Save draft")}
                </Button>
              </DialogFooter>
            </form>

            {/* Live preview */}
            <div className="hidden lg:flex flex-col">
              <div className="micro-label text-gold mb-2">Live Preview</div>
              <div className="border border-bone bg-white p-6 shadow-sm flex-1 overflow-y-auto" data-testid="letter-preview">
                <div className="border-t-4 border-gold pt-4 mb-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-xl tracking-tight font-bold">{(meta.company.name || "Rojas Property").toUpperCase()}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{meta.company.tagline}</div>
                  </div>
                  <div className="text-right text-[10px] text-slate-600 leading-snug">
                    <div>{meta.company.address_line1}</div>
                    <div>{meta.company.address_line2}</div>
                    <div className="text-gold mt-0.5">{meta.company.phone}</div>
                  </div>
                </div>
                <div className="border-t border-gold/40 mb-4" />
                <div className="text-xs text-slate-500 mb-3">{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
                {form.recipient_name && <div className="text-sm font-semibold">{form.recipient_name}</div>}
                {form.recipient_address && <div className="text-xs text-slate-600 whitespace-pre-wrap mb-3">{form.recipient_address}</div>}
                {previewBody.subject && <div className="font-display text-base mt-3 mb-3"><span className="text-gold uppercase tracking-widest text-[10px] mr-2">RE</span>{previewBody.subject}</div>}
                <div className="text-sm leading-relaxed text-ink whitespace-pre-wrap">{previewBody.body || <span className="text-slate-300 italic">Body preview will appear here…</span>}</div>
                <div className="mt-6 text-sm">Sincerely,</div>
                <div className="mt-6 text-sm font-semibold">{user?.name}</div>
                <div className="text-xs text-slate-500">@{user?.username} · Rojas Property</div>
                <div className="mt-6 pt-4 border-t-4 border-ink text-[10px] text-slate-500 leading-relaxed">
                  <div className="font-bold tracking-widest">{(meta.company.name || "ROJAS PROPERTY").toUpperCase()}</div>
                  <div className="text-gold">Phone {meta.company.phone} · Fax {meta.company.fax} · {meta.company.email}</div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={sendOpen} onOpenChange={(o) => { setSendOpen(o); if (!o) setSendTarget(null); }}>
        <DialogContent data-testid="send-letter-dialog">
          <DialogHeader><DialogTitle>Send via email</DialogTitle></DialogHeader>
          {sendTarget && (
            <div className="space-y-3 pt-2">
              <div className="bg-ivory border border-bone px-3 py-2 text-sm">
                <div className="font-medium">{sendTarget.subject_rendered || sendTarget.subject}</div>
                <div className="text-xs text-slate-500">PDF letterhead will be attached. The email body will use the same Rojas Property letterhead.</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">To (email address)</Label>
                <Input data-testid="send-to-email" type="email" required value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="recipient@example.com" />
              </div>
              <p className="text-xs text-slate-500">Resend sandbox: emails only deliver to your verified account email until you verify a domain.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button data-testid="send-letter-confirm" onClick={sendNow} disabled={sending} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold gap-1.5">
              <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={tplOpen} onOpenChange={(o) => { setTplOpen(o); if (!o) { setTplForm(BLANK_TEMPLATE); setTplEditing(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="template-dialog">
          <DialogHeader><DialogTitle>{tplEditing ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          <form onSubmit={submitTemplate} className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Name *</Label>
              <Input data-testid="template-name" required value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} placeholder="e.g. Late Rent Notice" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description (optional)</Label>
              <Input value={tplForm.description} onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} placeholder="When to use this template" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subject *</Label>
              <Input data-testid="template-subject" required value={tplForm.subject} onChange={(e) => setTplForm({ ...tplForm, subject: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Body *</Label>
              <Textarea data-testid="template-body" required rows={10} value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} placeholder="Dear {{tenant_name}}, …" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTplOpen(false)}>Cancel</Button>
              <Button data-testid="save-template-button" type="submit" className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold">
                {tplEditing ? "Save changes" : "Save template"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Letter delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this letter?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-letter" onClick={removeLetter} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template delete */}
      <AlertDialog open={!!tplConfirmDelete} onOpenChange={(o) => !o && setTplConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-template" onClick={removeTemplate} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
